/**
 * What the **All** theme knows about the boundary the pointer is over — the store the hover
 * card is built from, and the builder itself.
 *
 * Client change, 23 September 2026:
 *
 * > *"we should not have to click on a box — once a user starts hovering on the map, the
 * > pop-ups appear."*
 *
 * ## Why this is a store and not a prop
 *
 * The card belongs to the **boundary**, so the thing that opens it is Leaflet's own
 * `mouseover` on a `L.Path` that `HierarchyLayers` built. The counts, meanwhile, come from
 * five `useQuery` calls that `ActivityHover` owns — one per §F5.8 row, because each row is a
 * different grouping of the same endpoint. Those two live in different components and answer
 * at different times.
 *
 * Passing the data down would mean rebuilding every Leaflet layer whenever any grouping
 * landed, and binding the text at *build* time — so a card opened before its grouping arrived
 * would keep saying "loading" until the next pan. Instead `ActivityHover` writes here and
 * `HierarchyLayers` **reads at mouseover time** (`activityLookup`), which is the moment the
 * answer is actually needed. One indirection buys: no rebuild on data, and a card that is
 * right as soon as the fetch is.
 */
import { create } from 'zustand';

import type { ActivityGroup, ActivityUnit } from '../../lib/api';
import { escapeHtml } from '../../lib/html';
import { iconFor, iconSvg, tintFor } from './activityIcons';
import type { HierarchyKey } from './hierarchy';

/**
 * Which grouping of `/activities/by-country` answers for which §F5.8 row.
 *
 * One table rather than a branch, for the reason `HIERARCHY_ROWS` is one table: a row that
 * asked for the wrong grouping would draw a card of somebody else's numbers, and nothing
 * about the card would look wrong.
 */
export const GROUP_BY_ROW: Record<HierarchyKey, ActivityGroup> = {
  adm0: 'country',
  adm1: 'region',
  adm2: 'commune',
  wsheds: 'watershed',
  partners: 'partnership',
};

/** One grouping's answer, indexed by the unit id the boundary features carry. */
type UnitIndex = Record<string, ActivityUnit>;

interface ActivityHoverState {
  /**
   * Row → its grouping's answer. A row absent from this map has **not answered yet**; a row
   * present but missing an id has answered and that unit has no categorised activity (the
   * server omits a unit at zero). The card tells those two apart, so the store must too.
   */
  byRow: Partial<Record<HierarchyKey, UnitIndex>>;
  setRowUnits: (key: HierarchyKey, units: readonly ActivityUnit[]) => void;
  clearRow: (key: HierarchyKey) => void;
  clear: () => void;
}

export const useActivityHoverStore = create<ActivityHoverState>((set) => ({
  byRow: {},
  setRowUnits: (key, units) =>
    set((s) => {
      const index: UnitIndex = {};
      for (const unit of units) {
        index[unit.id] = unit;
        // Countries are asked for by ISO in half this app and by unit id in the other half,
        // and the boundary pack's admin-0 features have carried both. Indexing under each is
        // two entries per country, and it is the difference between a card and a bare name.
        if (unit.iso && !(unit.iso in index)) index[unit.iso] = unit;
      }
      return { byRow: { ...s.byRow, [key]: index } };
    }),
  clearRow: (key) =>
    set((s) => {
      if (!(key in s.byRow)) return {};
      const next = { ...s.byRow };
      delete next[key];
      return { byRow: next };
    }),
  clear: () => set((s) => (Object.keys(s.byRow).length === 0 ? {} : { byRow: {} })),
}));

/**
 * The counts for one boundary: the unit's row, `null` when its grouping has answered and
 * has nothing for it, `undefined` while that grouping is still in flight.
 */
export type ActivityLookup = (
  rowKey: HierarchyKey,
  unitId: string,
) => ActivityUnit | null | undefined;

/** The shared lookup. Read imperatively at `mouseover` — see the module docstring. */
export const activityLookup: ActivityLookup = (rowKey, unitId) => {
  const index = useActivityHoverStore.getState().byRow[rowKey];
  if (!index) return undefined;
  return index[unitId] ?? null;
};

/** The caller's `t`, narrowed to what the card asks of it. */
export type Translate = (key: string, options?: Record<string, unknown>) => string;

/** One category row: tinted glyph, label, count. Rows arrive sorted by the server. */
function rowHtml(cat: ActivityUnit['categories'][number]): string {
  return (
    `<div class="act-tip-row" data-testid="act-tip-row-${escapeHtml(cat.code)}"` +
    ` data-pathway="${escapeHtml(cat.pathway)}">` +
    `<span class="act-tip-ico" style="--act-tint:${tintFor(cat.pathway)}">` +
    `${iconSvg(iconFor(cat.code))}</span>` +
    `<span class="act-tip-label">${escapeHtml(cat.label)}</span>` +
    `<span class="act-tip-n">${escapeHtml(cat.n ?? 0)}</span>` +
    `</div>`
  );
}

/**
 * The crystal card for one boundary, as the HTML string Leaflet assigns to `innerHTML`.
 *
 * Three states, and each one is honest about itself:
 *
 * * **loading** (`entry === undefined`) — the name alone. A card with an empty list under it
 *   would read as "this unit has nothing", which is a different and possibly wrong sentence.
 * * **nothing here** (`entry === null`) — the card, with "No categorised activities" where
 *   the total goes. The reader pointed at it, so it answers.
 * * **counts** — the total and one row per category, in the order the server sorted them.
 *
 * Every piece of server text goes through `escapeHtml` (see `lib/html.ts`); the template
 * around it is ours and is meant to be markup.
 */
export function activityTipHtml(
  name: string,
  entry: ActivityUnit | null | undefined,
  t: Translate,
  prefix = '',
): string {
  const heading = `${prefix}${escapeHtml(name)}`;
  if (entry === undefined) return heading;
  const total =
    entry === null
      ? escapeHtml(t('map.activities.none'))
      : escapeHtml(t('map.activities.total', { count: entry.total }));
  const rows = entry ? entry.categories.map(rowHtml).join('') : '';
  return (
    `<div class="act-tip-head">` +
    `<span class="act-tip-eyebrow">${escapeHtml(t('map.activities.title'))}</span>` +
    `<span class="act-tip-name">${heading}</span>` +
    `<span class="act-tip-total">${total}</span>` +
    `</div>` +
    (rows ? `<div class="act-tip-rows">${rows}</div>` : '') +
    `<div class="act-tip-foot">${escapeHtml(t('map.activities.hint'))}</div>`
  );
}
